import { lazy } from 'react';
import type { ComponentType } from 'react';

const RELOAD_MARKER_KEY = 'chunk_reload_at';
const RELOAD_LOOP_WINDOW_MS = 30_000;

/**
 * React.lazy with a one-shot reload on chunk-load failure.
 *
 * Code-splitting introduces a failure mode the monolithic bundle never had:
 * after a deploy, a user whose open session loaded the previous main.js will
 * lazy-request chunk hashes that no longer exist on the pod (assets are served
 * from the pod itself, and a rolling deploy replaces them) → ChunkLoadError on
 * navigation. The fix is to reload once: the fresh index.html references the
 * new main.js and its matching chunk manifest.
 *
 * A sessionStorage timestamp guards against a reload loop when the failure is
 * NOT a stale deploy (e.g. the network is down): within a 30s window we rethrow
 * instead of reloading again, letting AppErrorBoundary render its error state.
 */
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory().catch((error: unknown) => {
      const lastReloadAt = Number(sessionStorage.getItem(RELOAD_MARKER_KEY) || 0);
      if (Date.now() - lastReloadAt > RELOAD_LOOP_WINDOW_MS) {
        sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now()));
        window.location.reload();
        // Never resolves — the page is reloading; keeps Suspense showing its
        // fallback instead of flashing an error during the reload.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    })
  );
}
